
import { Component, Emit, Prop, Vue } from 'vue-property-decorator'
import { Observer } from 'mobx-vue'
import { BaseCouponItem } from '@xt/client/types/coupon'
import { formatCouponInfo, FormatCouponInfo, getCouponUseInfo, EnumCouponTimeRule } from '@xt/client/utils/coupon/index'
import { EnumCouponStatus, EnumCouponType, EnumPreferentialType, EnumProductType, EnumProductTypeQuery } from '@xt/client/enums'
import LaunchWeapp from '@xt/mobile/components/launchWeapp/index.vue'
import WeOpenTag from '@xt/mobile/configs/wx-open-tag'
import { reportEntryLog } from '@xt/client/utils/log'

@Observer
@Component({
  components: {
    LaunchWeapp
  }
})
export default class GiftCoupon extends Vue {
  @Prop({ required: true }) data: BaseCouponItem
  @Prop({ required: true }) success: boolean
  @Prop({ default: false }) isLast: boolean
  formatCouponInfo: FormatCouponInfo

  get UserStore() {
    return this.$store.$storeUser
  }

  get isEmbeddedH5Page() {
    return this.$store.$global.isEmbeddedH5Page
  }

  get couponType(): 'FullReduction' | 'Discount' | 'Exchange' | 'Companion' {
    if (this.data.preferentialType === EnumPreferentialType.StudyBuff && this.data.couponType === EnumCouponType.Companion) {
      return 'Companion'
    }

    switch (this.data.preferentialType) {
      case EnumPreferentialType.FullReduction:
        return 'FullReduction'
      case EnumPreferentialType.Discount:
        return 'Discount'
      case EnumPreferentialType.Exchange:
        return 'Exchange'
      default:
        reportEntryLog('debug.coupon-type.error', {
          t: this.data.preferentialType,
          c: this.data.couponType
        })
        throw new Error('couponType error' + this.data.preferentialType)
    }
  }

  get couponUseInfo() {
    return getCouponUseInfo(this.data, this.success ? EnumCouponTimeRule.PositionRule3 : EnumCouponTimeRule.PositionRule1)
  }

  get couponStatus() {
    if (this.success) return { text: '', style: '' }

    return this.data.stockQty === 0 ? { text: '已发完', style: 'black' } : { text: '发放失败，已达领取上限', style: 'red' }
  }

  get EnumCouponType() {
    return EnumCouponType
  }

  get EnumProductTypeQuery() {
    return EnumProductTypeQuery
  }

  get unUsed() {
    return this.data.status === EnumCouponStatus.UnUsed
  }

  get isWenfangCoupon() {
    return this.data.couponType === EnumCouponType.PencilStore
  }

  get isExchangeCoupon() {
    return this.data.couponType === EnumCouponType.Exchange
  }

  get isStudyBuffCoupon() {
    return this.data.couponType === EnumCouponType.Companion
  }

  get EnumCouponTimeRule() {
    return EnumCouponTimeRule
  }

  /** 自己的券 没有赠给别人 */
  get isSelfCoupon() {
    return this.data.ownerId === this.UserStore.UserInfo.id
  }

  /** 首次赠送 */
  get firstGiveCoupon() {
    return this.data.ownerId === this.data.memberId
  }

  /** 已领取：手机号 */
  get showReceivePhoneTip() {
    // 文房券 && 拥有人id和自己的id一致 && 已经领取了
    return this.isWenfangCoupon && this.isSelfCoupon && this.data.wenfangReceived
  }

  get haveOwnedText() {
    if (!this.isSelfCoupon) {
      return '已赠送'
    }

    switch (this.data.status) {
      // 是文房券 && 赠给了别人  如果是未使用  还会展示已使用
      case EnumCouponStatus.UnUsed:
      case EnumCouponStatus.Used:
        return '已使用'
      case EnumCouponStatus.InValid:
        return '已失效'
      case EnumCouponStatus.Expired:
        return '已过期'
    }
  }

  get username() {
    return this.$store.$global.production ? WeOpenTag.applictions.uc : WeOpenTag.applictions.uc_trial
  }

  get wenfangUsername() {
    return WeOpenTag.applictions.wenfang
  }

  get wenfangPath() {
    return WeOpenTag.wenfangPath.wenfang_dashboard
  }

  get envVersion() {
    return this.$store.$global.production ? 'release' : 'trial'
  }

  // 满足用券时间要求
  get isTimeStart() {
    const now = this.$ajax.serviceDate()

    return now.isAfter(this.data.validStartTime)
  }

  handleClickToUse() {
    if (!this.isTimeStart) {
      this.$toast('用券时间未开始')
      return
    }
  }

  handleClickUseInfo() {
    this.$emit('click-use-info', { list: this.couponUseInfo, coupon: this.data })
  }

  goUseCoupon() {
    // 校验一下用券时间
    const now = this.$ajax.serviceDate()
    if (now.isBefore(this.data.validStartTime)) {
      this.$toast('用券时间未开始')
      return
    }

    this.$emit('close')
    if (this.isStudyBuffCoupon) {
      // 如果在小程序内则去小程序
      if (this.$store.$global.isEmbeddedH5Page) {
        wx.miniProgram.switchTab({
          url: '/pages/toclass/index'
        })
      } else {
        this.$router.push({
          path: '/toclass'
        })
      }
    } else {
      const product = this.data.exchangeProducts[0]
      if (product.productType === EnumProductType.fullstage) {
        this.$router.push({
          path: product.activityPageUrl
        })
        return
      }
      this.$router.push({
        path: `/commodity/${product.productId}`
      })
    }
  }

  onShareCoupon() {
    if (!this.isEmbeddedH5Page) return

    wx.miniProgram.navigateTo({
      url: `/pages/coupon/share/user/index?couponCode=${this.data.couponCode}`,
      success: () => {
        this.$emit('close')
      }
    })
  }

  onUseCoupon() {
    // 校验一下用券时间
    const now = this.$ajax.serviceDate()
    if (now.isBefore(this.data.validStartTime)) {
      this.$toast('用券时间未开始')
      return
    }

    if (!this.isEmbeddedH5Page) return

    wx.miniProgram.navigateTo({
      url: `/pages/coupon/packs/index?couponId=${this.data.id}`,
      success: () => {
        this.$emit('close')
      }
    })
  }

  @Emit('close')
  onLaunchWeapp() {}

  created() {
    this.formatCouponInfo = formatCouponInfo(this.data, this.success ? EnumCouponTimeRule.PositionRule3 : EnumCouponTimeRule.PositionRule1)
  }
}
